import { useEffect, useState, type FormEvent } from 'react';
import { Button, Field, Input, Panel, PanelBody, PanelHeader, Select } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { api, ApiError } from '@/lib/api';
import { compareDecimal, formatCount, formatPct, parseLineFieldInput } from '@/lib/sales-targets';
import type { PriceTier, SalesTargetSettingsInput, SalesTargetsView } from '@/lib/types';

/**
 * Sales targets → cycle settings: the card-rate price tier (resolved by id,
 * never by name) and the default travel %. `PATCH /sales-targets/settings/
 * :cycleId` with only the changed keys; the parent re-reads the view after.
 *
 * Tiers come from `GET /price-tiers`. Only active tiers are offered (writes
 * reject inactive ones), plus the stored tier when it has since been retired,
 * so the Select never shows a value it cannot name.
 */

interface Props {
  view: SalesTargetsView;
  canEdit: boolean;
  onChanged: () => void;
}

const TRAVEL_FIELD = { kind: 'percent', min: 0, max: 100, decimals: 2 } as const;

export function SalesTargetSettingsCard({ view, canEdit, onChanged }: Props) {
  const settings = view.settings;
  const [tiers, setTiers] = useState<PriceTier[] | null>(null);
  const [tiersError, setTiersError] = useState<string | null>(null);

  /** `undefined` = untouched (show the server's value). */
  const [tierDraft, setTierDraft] = useState<string | undefined>(undefined);
  const [travelDraft, setTravelDraft] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  /*
   * A different cycle (or new server values) starts from the server again.
   * Adjusted during render rather than in an effect, so no stale draft paints.
   */
  const resetKey = `${view.cycle.id}|${settings.priceTierId ?? ''}|${settings.defaultTravelPercent}`;
  const [seenKey, setSeenKey] = useState(resetKey);
  if (seenKey !== resetKey) {
    setSeenKey(resetKey);
    setTierDraft(undefined);
    setTravelDraft(undefined);
    setFieldErrors({});
    setFormError(null);
  }

  useEffect(() => {
    if (!canEdit) return;
    const controller = new AbortController();
    api
      .listPriceTiers(controller.signal)
      .then((body) => {
        if (controller.signal.aborted) return;
        setTiers(body.data);
        setTiersError(null);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setTiersError(error instanceof Error ? error.message : 'Price tiers could not be loaded.');
      });
    return () => controller.abort();
  }, [canEdit]);

  const serverTier = settings.priceTierId === null ? '' : String(settings.priceTierId);
  const tierValue = tierDraft ?? serverTier;
  const serverTravel = formatCount(settings.defaultTravelPercent);
  const travelValue = travelDraft ?? serverTravel;
  const travelParsed = parseLineFieldInput(TRAVEL_FIELD, travelValue);

  const tierChanged = tierValue !== serverTier;
  const travelChanged =
    travelDraft !== undefined &&
    !(
      travelParsed.ok &&
      travelParsed.value !== null &&
      compareDecimal(travelParsed.value, settings.defaultTravelPercent) === 0
    );
  const dirty = tierChanged || travelChanged;

  const tierOptions = (tiers ?? [])
    .filter((tier) => tier.isActive || String(tier.id) === serverTier)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const storedTierMissing =
    serverTier !== '' && tiers !== null && !tierOptions.some((tier) => String(tier.id) === serverTier);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !dirty) return;

    const errors: Record<string, string> = {};
    const body: SalesTargetSettingsInput = {};
    if (tierChanged) body.priceTierId = tierValue === '' ? null : Number(tierValue);
    if (travelChanged) {
      if (!travelParsed.ok) errors.defaultTravelPercent = travelParsed.error;
      else if (travelParsed.value === null) errors.defaultTravelPercent = 'Enter a percentage.';
      else body.defaultTravelPercent = travelParsed.value;
    }
    setFieldErrors(errors);
    setFormError(null);
    if (Object.keys(errors).length > 0) return;

    setBusy(true);
    try {
      await api.updateSalesTargetSettings(view.cycle.id, body);
      setTierDraft(undefined);
      setTravelDraft(undefined);
      onChanged();
    } catch (error) {
      if (error instanceof ApiError) {
        const routed = error.routeFieldErrors(['priceTierId', 'defaultTravelPercent']);
        setFieldErrors(routed.fields);
        setFormError(
          routed.unroutable.length > 0
            ? routed.unroutable.join(' ')
            : Object.keys(routed.fields).length > 0
              ? null
              : error.message,
        );
      } else {
        setFormError(error instanceof Error ? error.message : 'Something went wrong.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!canEdit) {
    return (
      <Panel aria-labelledby="sales-target-settings-title">
        <PanelHeader id="sales-target-settings-title" title="Settings" />
        <PanelBody>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
            <dt className="text-fg-muted">Card rate tier</dt>
            <dd className="text-right break-words text-fg">{settings.priceTierName ?? '—'}</dd>
            <dt className="text-fg-muted">Default travel</dt>
            <dd className="text-right text-fg tabular-nums">
              {formatPct(settings.defaultTravelPercent)}
            </dd>
          </dl>
        </PanelBody>
      </Panel>
    );
  }

  return (
    <Panel aria-labelledby="sales-target-settings-title">
      <PanelHeader id="sales-target-settings-title" title="Settings" />
      <PanelBody>
        <form onSubmit={(event) => void submit(event)} className="grid gap-4" noValidate>
          {formError && (
            <Notice tone="danger" role="alert">
              {formError}
            </Notice>
          )}
          {tiersError && (
            <Notice tone="danger" role="alert">
              {tiersError}
            </Notice>
          )}
          <Field label="Card rate tier" error={fieldErrors.priceTierId}>
            {(props) => (
              <Select
                {...props}
                value={tierValue}
                invalid={Boolean(fieldErrors.priceTierId)}
                disabled={busy || tiers === null}
                onChange={(event) => setTierDraft(event.target.value)}
              >
                <option value="">None</option>
                {storedTierMissing && (
                  <option value={serverTier}>{settings.priceTierName ?? `Tier ${serverTier}`}</option>
                )}
                {tiers === null && serverTier !== '' && (
                  <option value={serverTier}>{settings.priceTierName ?? `Tier ${serverTier}`}</option>
                )}
                {tierOptions.map((tier) => (
                  <option key={tier.id} value={String(tier.id)}>
                    {tier.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Default travel %" error={fieldErrors.defaultTravelPercent}>
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={travelValue}
                invalid={Boolean(fieldErrors.defaultTravelPercent)}
                disabled={busy}
                onChange={(event) => setTravelDraft(event.target.value)}
                className="tabular-nums"
              />
            )}
          </Field>
          <div className="flex justify-end gap-2">
            {dirty && (
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setTierDraft(undefined);
                  setTravelDraft(undefined);
                  setFieldErrors({});
                  setFormError(null);
                }}
              >
                Cancel
              </Button>
            )}
            <Button variant="primary" type="submit" loading={busy} disabled={!dirty}>
              Save
            </Button>
          </div>
        </form>
      </PanelBody>
    </Panel>
  );
}
